import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useRouter } from "@tanstack/react-router";

/**
 * Renders a self-referencing canonical <link> that always points to the
 * current page's clean URL (origin + pathname only, all query params stripped).
 *
 * This tells Google which URL is the "master copy" of the page, preventing
 * UTM / tracking parameters (?utm_source=, ?cid=, ?tid=, etc.) from being
 * treated as separate pages and diluting ranking signals.
 *
 * @param pathname - Optional explicit pathname. When omitted the component
 *   derives it from the router's current location (SSR-safe).
 *
 * WARNING: Do NOT use this component inside routes with `ssr: true`.
 * Server render emits a relative href (origin is ""), while client hydration
 * emits an absolute href — causing a React hydration mismatch on the `href`
 * attribute. For full-SSR routes, build the canonical inline in the route's
 * `head` option using a server-provided origin (e.g. loaderData.canonicalOrigin).
 * This component is safe for SPA-only routes and `ssr: "data-only"` routes
 * (where the component only renders client-side).
 */
export function CanonicalTag({ pathname }: { pathname?: string }) {
  const router = useRouter();
  const path = pathname ?? router.state.location.pathname;
  const [origin, setOrigin] = useState("");
  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);
  const canonicalUrl = `${origin}${path}`;

  return (
    <Helmet>
      <link rel="canonical" href={canonicalUrl} />
    </Helmet>
  );
}
